import { useEffect, useState, useCallback, useRef } from "react";
import { NAVY, GOLD } from "../styles/palette";
import { useIsMobile } from "../lib/useIsMobile";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { getPerVisit, DEO_PER_VISIT } from "../lib/pricing";
import { JobPhoto } from "./JobPhoto";
import { fetchClientVisits } from "../lib/visitTimes";
import { localDateStr } from "../lib/date";
import { SKIP_REASONS, skipReasonLabel, SKIP_NOTE_MAX } from "../lib/constants";

// ═══════════════════════════════════════════════════════════════════
// JOB DETAIL MODAL  (job-centric)
// ═══════════════════════════════════════════════════════════════════
// A JOB = one client, one day (the job_headers entity). Its SERVICES
// (cleanup / deo) are the line-items. This modal is framed around the
// whole job: job status + job DURATION (first service start → last
// service completion, from the header rollup) up top, then every
// service listed equally with its own status, duration, and actions.
// No single service is privileged, regardless of which one was clicked
// to open it.
//
//   • "Start Job"          → starts every scheduled service at once
//   • per service          → Complete / Skip (+credit) / Reschedule
//   • tech + notes         → job-level (apply to the whole visit)
//
// Props
//   job          — the clicked `jobs` row (entry point; we derive the
//                  job from its client_id + scheduled_date)
//   clients, technicians, pricing, onClose, onUpdated, techScoped, showToast
// ═══════════════════════════════════════════════════════════════════

const TERMINAL_STATUSES = ["completed", "skipped", "missed", "cancelled", "rescheduled"];


function statusBadgeClass(status) {
  return status === "completed" ? "badge-green"
       : status === "started"   ? "badge-blue"
       : status === "partial"   ? "badge-gold"
       : status === "scheduled" ? "badge-gold"
       : status === "skipped"   ? "badge-gray"
       : status === "missed"    ? "badge-red"
       : status === "cancelled" ? "badge-gray"
       : "badge-gray";
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}
function fmtTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}
function durationMins(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return isNaN(ms) || ms < 0 ? null : Math.round(ms / 60000);
}
function fmtDuration(mins) {
  if (mins == null) return "—";
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function svcLabel(t) { return t === "deo" ? "💨 Deodorizer" : "🧹 Cleanup"; }
function svcShort(t) { return t === "deo" ? "Deodorizer" : "Cleanup"; }

export function JobDetailModal({ job, clients, technicians, pricing, onClose, onUpdated, techScoped = false, showToast, presentation = "modal", onDirtyChange, onBusyChange, onOpenClient, headerSlot }) {
  const clientId = job.client_id;
  const day      = job.scheduled_date;
  // A JOB's identity is (client_id, scheduled_date, SOURCE) — auto recurring, a manual
  // one-off, and a reschedule destination can all share a client+day as SEPARATE jobs.
  // Everything job-wide (fetch + job-level writes) must be scoped to this one source.
  const source   = job.source || "auto";
  const client   = (clients || []).find(c => c.id === clientId);
  const isMobile = useIsMobile();
  const isPanel = presentation === "panel";
  const TimingSection = isPanel ? "details" : "div";
  const isDialog = !isPanel || isMobile;
  const panelRef = useRef(null);
  const headingRef = useRef(null);
  const requestRef = useRef(0);

  const [header,   setHeader]   = useState(null);  // job_headers row (derived rollup)
  const [services, setServices] = useState([]);    // full jobs rows for this client-day
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [loadError, setLoadError] = useState("");

  // per-service sub-flows (which service is being skipped / rescheduled)
  const [skipFor,    setSkipFor]    = useState(null);
  const [skipReason, setSkipReason] = useState(SKIP_REASONS[0]);
  const [skipNote, setSkipNote] = useState("");
  const [skipCredit, setSkipCredit] = useState(false);
  const [reschedFor,  setReschedFor]  = useState(null);
  const [reschedDate, setReschedDate] = useState("");

  // job-level editable fields (apply to the whole visit)
  const [techId,     setTechId]     = useState("");
  const [notes,      setNotes]      = useState("");
  const [visitNotes, setVisitNotes] = useState("");
  const [etaMode,    setEtaMode]    = useState(false);
  const [etaMinutes, setEtaMinutes] = useState(30);
  // GPS "time on site" for THIS visit (this client on this day) — the dwell time the
  // field app auto-tracks from the tech's location. Kept SEPARATE from the job's
  // start→complete duration, which is driven by the Start Job / Complete buttons.
  const [onSiteMin,  setOnSiteMin]  = useState(null);
  const [onSiteTech, setOnSiteTech] = useState(null);
  const seededRef = useRef(false); // seed tech/notes ONCE per opened job; never clobber edits on reload

  const reload = useCallback(async () => {
    const request = ++requestRef.current;
    const h = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` };
    if (isPanel) setLoadError("");
    try {
      const readRows = async (path) => {
        const response = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, { headers: h });
        if (isPanel && !response.ok) throw new Error("Visit details could not be loaded. Please try again.");
        const rows = await response.json();
        if (isPanel && !Array.isArray(rows)) throw new Error("Visit details could not be loaded. Please try again.");
        return rows;
      };
      const [hRows, sRows] = await Promise.all([
        readRows(`job_headers?client_id=eq.${clientId}&scheduled_date=eq.${day}&source=eq.${source}&select=*&limit=1`),
        readRows(`jobs?client_id=eq.${clientId}&scheduled_date=eq.${day}&source=eq.${source}&status=neq.rescheduled&select=*`),
      ]);
      if (request !== requestRef.current) return;
      const hdr = Array.isArray(hRows) ? (hRows[0] || null) : null;
      const svc = Array.isArray(sRows)
        ? sRows.slice().sort((a, b) => ((a.service_type === "deo" ? 1 : 0) - (b.service_type === "deo" ? 1 : 0)))
        : [];
      setHeader(hdr);
      setServices(svc);
      const primary = svc[0] || job;
      // seed the job-level editable fields ONCE per opened job — later reloads (after
      // an action) must not clobber whatever the operator has typed but not yet saved.
      if (!seededRef.current) {
        seededRef.current = true;
        setTechId((hdr?.technician_id) || primary.technician_id || "");
        setNotes(primary.notes || "");
        setVisitNotes(primary.visit_notes || "");
      }
    } catch (e) {
      if (isPanel && request === requestRef.current) setLoadError("Visit details could not be loaded. Please try again.");
    }
    finally { if (request === requestRef.current) setLoading(false); }
  }, [clientId, day, source, isPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    seededRef.current = false;
    setLoading(true);
    reload();
    return () => { requestRef.current += 1; };
  }, [reload]);
  // Phone: this is the tallest sheet in the app (client grid + timeline + services + ETA +
  // tech + two textareas). The document scrolls on phones, so a swipe that reaches the end
  // of the sheet (or lands on the backdrop) scrolls the page behind it. Lock the body while
  // open with the theme's body.sheet-open class (position:fixed), pinning the current
  // scroll offset and restoring it on close.
  useEffect(() => {
    if (!isMobile || typeof document === "undefined") return undefined;
    const body = document.body;
    if (body.classList.contains("sheet-open")) return undefined; // another sheet already owns the lock
    const y = window.scrollY || 0;
    body.classList.add("sheet-open");
    body.style.top = `-${y}px`;
    return () => {
      body.classList.remove("sheet-open");
      body.style.top = "";
      window.scrollTo(0, y);
    };
  }, [isMobile]);
  // reset sub-flows when a different job is opened
  useEffect(() => {
    setSkipFor(null); setReschedFor(null); setEtaMode(false); setError("");
  }, [clientId, day, source]);

  // GPS time-on-site for THIS client+day (independent of Start/Complete). The field
  // app logs one client_visit_times row per GPS-detected visit; match by calendar day
  // (local) and sum any same-day dwell. Blank until a visit exists for this day.
  useEffect(() => {
    let alive = true;
    setOnSiteMin(null); setOnSiteTech(null);
    if (!clientId || !day) return;
    fetchClientVisits(clientId).then((rows) => {
      if (!alive) return;
      const sameDay = (rows || []).filter((v) => v.created_at && localDateStr(new Date(v.created_at)) === day);
      if (!sameDay.length) return;
      const secs = sameDay.reduce((a, v) => a + (Number(v.seconds) || 0), 0);
      if (secs > 0) setOnSiteMin(Math.round(secs / 60));
      const tid = sameDay.find((v) => v.technician_id)?.technician_id || null;
      setOnSiteTech((technicians || []).find((t) => t.id === tid)?.name || null);
    });
    return () => { alive = false; };
  }, [clientId, day, technicians]);

  // ── Derived job-level values (prefer the header rollup; fall back to services) ──
  const startedAts   = services.map(s => s.started_at).filter(Boolean);
  const completedAts  = services.map(s => s.completed_at).filter(Boolean);
  // Pair start/end as a UNIT: when a header exists, trust its rollup (it leaves
  // completed_at NULL while in progress — never borrow a finished service's time).
  const jobStarted    = header ? header.started_at   : (startedAts.length   ? startedAts.slice().sort()[0] : null);
  const jobCompleted  = header ? header.completed_at : (completedAts.length ? completedAts.slice().sort().slice(-1)[0] : null);
  const allTerminal   = services.length > 0 && services.every(s => TERMINAL_STATUSES.includes(s.status));
  const jobStatus     = header?.status
    || (services.length === 0 ? job.status
      : allTerminal ? (services.every(s => s.status === "completed") ? "completed" : "skipped")
      : services.some(s => s.status === "started") ? "started"
      : services.some(s => TERMINAL_STATUSES.includes(s.status)) ? "partial"
      : "scheduled");
  // Job duration = first start → last completion (only meaningful once finished).
  const jobDuration   = header?.duration_mins ?? durationMins(jobStarted, jobCompleted);
  const inProgress    = jobStatus === "started" || (!!jobStarted && !jobCompleted && !allTerminal);
  const techName      = header?.technician_name
    || (services.find(s => s.technician_name)?.technician_name)
    || job.technician_name || null;
  const photoPath     = header?.photo_url || (services.find(s => s.photo_url)?.photo_url) || null;
  const scheduledSvc  = services.filter(s => s.status === "scheduled");

  // "dirty" by comparison against the server values (no stale dirty-flag state).
  // Use the SAME basis as the seed (services[0] || job) so an empty service set
  // doesn't read as dirty against the clicked job's values.
  const primaryRow  = services[0] || job;
  const serverTech  = (header?.technician_id) || primaryRow.technician_id || "";
  const serverNotes = primaryRow.notes || "";
  const serverVisit = primaryRow.visit_notes || "";
  const techDirty   = techId !== serverTech;
  const notesDirty  = notes !== serverNotes || visitNotes !== serverVisit;
  // A failed refresh must not make existing edits safe to discard. Before the
  // first successful read there is no editable form and therefore no draft.
  const hasUnsavedChanges = Boolean(seededRef.current && (techDirty || notesDirty || skipFor || reschedFor || etaMode));

  // The Schedule parent uses this to guard changing the selected visit or date.
  // Default modal callers retain their existing behavior.
  useEffect(() => {
    if (isPanel && onDirtyChange) onDirtyChange(hasUnsavedChanges);
  }, [isPanel, hasUnsavedChanges, onDirtyChange]);
  useEffect(() => () => { if (isPanel && onDirtyChange) onDirtyChange(false); }, [isPanel, onDirtyChange]);
  useEffect(() => {
    if (isPanel && onBusyChange) onBusyChange(saving);
  }, [isPanel, saving, onBusyChange]);
  useEffect(() => () => { if (isPanel && onBusyChange) onBusyChange(false); }, [isPanel, onBusyChange]);

  const canLeave = useCallback(() => {
    if (!isPanel) return true;
    if (saving) {
      showToast && showToast("Please wait for this visit to finish saving.");
      return false;
    }
    return !hasUnsavedChanges || window.confirm("Discard your unsaved changes to this visit?");
  }, [isPanel, saving, hasUnsavedChanges, showToast]);
  const requestClose = useCallback(() => {
    if (canLeave()) onClose && onClose();
  }, [canLeave, onClose]);
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;

  useEffect(() => {
    if (!isPanel) return;
    const previousFocus = document.activeElement;
    headingRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      }
      if (event.key !== "Tab" || !isMobile) return;
      const focusable = [...(panelRef.current?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]') || [])]
        .filter(element => element.getClientRects().length > 0);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); headingRef.current?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (isMobile) document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [isPanel, isMobile, clientId, day, source]);

  const tech       = (technicians || []).find(t => t.id === techId);
  const mapUrl     = client?.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.address + " " + (client.zip || ""))}`
    : null;

  // Phone-only presentation tokens (desktop keeps the original text links / btn-sm / borderLeft stats).
  const pillLink  = { display:"inline-flex", alignItems:"center", gap:6, minHeight:36, padding:"8px 12px", borderRadius:999, background:"var(--accent-weak, #e8f1fd)", color:NAVY, fontSize:14, fontWeight:600, textDecoration:"none" };
  const statCell  = { textAlign:"center", padding:"10px 4px 0", borderTop:"1px solid #e3e8ef", flex:"1 1 50%", minWidth:0 };
  const actionBtn = isMobile ? { flex:"1 1 auto", minHeight:40 } : undefined;

  function creditAmountFor(svc) {
    if (!client || !pricing) return 0;
    return svc.service_type === "deo"
      ? (pricing?.deoPerVisit?.[client.deo_frequency || "biweekly"] ?? DEO_PER_VISIT[client.deo_frequency || "biweekly"] ?? 0)
      : getPerVisit(client.frequency || "weekly", client.dogs || 1, pricing);
  }

  // ── write helper ────────────────────────────────────────────────
  async function patch(path, body) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
      method: "PATCH",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify(body),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || `Save failed (${res.status})`); }
  }
  async function afterWrite(msg) { if (msg && showToast) showToast(msg); await reload(); onUpdated && onUpdated(); }

  // ── job-level: start the whole job (all scheduled services) ──────
  async function handleStartJob() {
    if (!window.confirm("Start this job? This stamps the start time on all of its scheduled services.")) return;
    setSaving(true); setError("");
    try {
      await patch(`jobs?client_id=eq.${clientId}&scheduled_date=eq.${day}&source=eq.${source}&status=eq.scheduled`,
        { status: "started", started_at: new Date().toISOString() });
      await afterWrite("Job started");
    } catch (e) { setError(e.message || "Could not start job"); }
    finally { setSaving(false); }
  }

  // ── per-service: complete ────────────────────────────────────────
  async function handleCompleteService(svc) {
    setSaving(true); setError("");
    try {
      const now = new Date().toISOString();
      await patch(`jobs?id=eq.${svc.id}`,
        { status: "completed", completed_at: now, ...(svc.started_at ? {} : { started_at: now }) });
      await afterWrite(`${svcShort(svc.service_type)} completed ✓`);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  // ── per-service: skip (+ optional credit, skip-first then credit) ─
  async function handleConfirmSkip(svc) {
    if (!skipReason) { setError("Pick a skip reason"); return; }
    const amt = creditAmountFor(svc);
    setSaving(true); setError("");
    // SKIP FIRST — if the status change is rejected we never post a credit.
    try {
      await patch(`jobs?id=eq.${svc.id}`,
        { status: "skipped", skipped_reason: skipReason, skip_note: skipNote.trim() || null, credited: false, completed_at: new Date().toISOString() });
    } catch (e) { setError(e.message); setSaving(false); return; }

    if (!skipCredit) { setSkipFor(null); await afterWrite(`Skipped — ${skipReasonLabel(skipReason)}`); setSaving(false); return; }
    if (!client || !pricing || !(amt > 0)) {
      setSkipFor(null);
      await afterWrite(null);
      setError("Skipped, but no credit applied — client plan/pricing unavailable. Issue the credit from Billing.");
      setSaving(false); return;
    }
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/apply-credit`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "apply", client_id: clientId, amount: amt,
          description: `Skipped visit credit — ${day} (${skipReasonLabel(skipReason)})`,
          job_id: svc.id, // real row → apply-credit idempotency prevents a double-post
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.error) throw new Error(body?.error || `HTTP ${res.status}`);
      await patch(`jobs?id=eq.${svc.id}`, { credited: true });
      setSkipFor(null);
      await afterWrite(body.idempotent ? "Skipped — a credit already existed for this visit" : `Skipped — $${amt.toFixed(2)} credited`);
    } catch (e) {
      setSkipFor(null);
      await afterWrite(null);
      setError(`Skipped, but credit failed: ${e.message || e}. Retry from Billing.`);
    }
    setSaving(false);
  }

  // ── per-service: reschedule (RPC tombstone; raw date PATCH is blocked) ─
  async function handleReschedule(svc) {
    if (!reschedDate) { setError("Pick a date to reschedule to"); return; }
    if (reschedDate <= day) { setError("Pick a date later than the current one"); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/job_transition`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ p_service_id: svc.id, p_action: "reschedule_service", p_payload: { to_date: reschedDate } }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) { setError((data && (data.message || data.error || data.hint)) || `Reschedule failed (${res.status})`); setSaving(false); return; }
      setReschedFor(null);
      await afterWrite(`${svcShort(svc.service_type)} rescheduled to ${reschedDate}`);
    } catch (e) { setError("Reschedule failed — check connection"); }
    finally { setSaving(false); }
  }

  // ── job-level: tech reassign + notes (apply to the whole visit) ──
  async function handleApplyTech() {
    setSaving(true); setError("");
    try {
      await patch(`jobs?client_id=eq.${clientId}&scheduled_date=eq.${day}&source=eq.${source}&status=neq.rescheduled`,
        { technician_id: techId || null, technician_name: tech?.name || null });
      await afterWrite(techId ? `Reassigned to ${tech?.name}` : "Unassigned");
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }
  async function handleSaveNotes() {
    setSaving(true); setError("");
    try {
      await patch(`jobs?client_id=eq.${clientId}&scheduled_date=eq.${day}&source=eq.${source}&status=neq.rescheduled`,
        { notes: notes || null, visit_notes: visitNotes || null });
      await afterWrite("Notes saved");
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }
  async function handleSendETA() {
    const name = client?.name || job.client_name;
    if (isPanel) { setSaving(true); setError(""); }
    try {
      // Actually SEND it — send-eta texts the client (if opted in + Twilio live)
      // else emails them; org-scoped via the caller's JWT.
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-eta`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        // Send the RESOLVED tech name (header → service → job), never the operator's
        // login/email — so the customer text reads "Ruben", not "rucollazo123".
        body: JSON.stringify({ client_id: clientId, minutes: Number(etaMinutes), technician_name: techName }),
      }).then(r => r.json()).catch(() => ({}));
      // "push" is a SUCCESS, not a failure: the customer has the app and the
      // single-channel rule (sql/075) deliberately skips the text so they get one
      // notice, not two. Without this branch the operator would be told the send
      // failed every time an app customer was notified perfectly.
      const msg = res?.channel === "sms"
        ? `ETA texted to ${name}: tech arriving in ~${etaMinutes} min`
        : res?.channel === "email"
        ? `ETA emailed to ${name}: tech arriving in ~${etaMinutes} min`
        : res?.channel === "push"
        ? `ETA sent to ${name} in the app: tech arriving in ~${etaMinutes} min`
        : `ETA logged for ${name} (~${etaMinutes} min) — no text/email could be sent`;
      // Mirror the field app: record the eta_sent event in client history.
      await fetch(`${SUPABASE_URL}/rest/v1/client_history`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ client_id: clientId, event_type: "eta_sent", description: msg, new_value: `${etaMinutes} min` }),
      });
      showToast && showToast(msg);
      setEtaMode(false);
    } catch (e) { setError("Could not send ETA"); }
    finally { if (isPanel) setSaving(false); }
  }

  return (
    <div className={isPanel ? `sc-job-detail sc-job-detail--${isMobile ? "dialog modal-overlay" : "embedded"}` : "modal-overlay"} onClick={e => isDialog && e.target === e.currentTarget && requestClose()}>
      <div ref={panelRef} className="modal" style={isPanel && !isMobile ? { maxWidth: "none", maxHeight: "none", boxShadow: "none", borderRadius: 0 } : { maxWidth: 640 }} role={isPanel ? (isDialog ? "dialog" : "region") : undefined} aria-modal={isPanel && isDialog ? true : undefined} aria-labelledby={isPanel ? "sc-visit-detail-heading" : undefined}>
        <div className="modal-header">
          <div>
            {isPanel && <div className="sc-detail-eyebrow">Visit details</div>}
            <h3 ref={headingRef} id={isPanel ? "sc-visit-detail-heading" : undefined} tabIndex={isPanel ? -1 : undefined}>{client?.name || job.client_name}{!isPanel && ` — ${day}`}</h3>
            {isPanel && <div className="sc-detail-date">{new Date(`${day}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" })}</div>}
            <div style={{fontSize:11,color:"#8993a4",marginTop:3,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <span style={{textTransform:"capitalize"}}>{isPanel ? (source === "manual" ? "One-off" : source === "auto" ? "Recurring" : "Rescheduled") : (job.source === "manual" ? "Manual" : "Recurring")} {isPanel ? "visit" : "job"}</span>
              <span>·</span>
              <span>{services.length || "—"} service{services.length === 1 ? "" : "s"}</span>
              <span>·</span>
              <span className={`badge ${statusBadgeClass(jobStatus)}`} style={{textTransform:"capitalize"}}>{jobStatus === "empty" ? "No services" : jobStatus}</span>
            </div>
            {isPanel && headerSlot && <div className="sc-detail-header-slot">{headerSlot}</div>}
          </div>
          <button className="close-btn" onClick={requestClose} disabled={isPanel && saving} aria-label={isPanel ? "Close visit details" : undefined}>×</button>
        </div>

        <div className="modal-body" aria-busy={isPanel ? loading : undefined}>
          {isPanel && loading && <div className="sc-detail-loading" role="status">Loading visit details…</div>}
          {isPanel && !loading && loadError && (
            <div className="sc-detail-load-error" role="alert">
              <p>{loadError}</p>
              <button className="btn btn-ghost btn-sm" onClick={() => { setLoading(true); reload(); }}>Try again</button>
            </div>
          )}
          {(!isPanel || (!loading && !loadError)) && <>
          {/* ── Standing service note ───────────────────────────────
              The CLIENT's current note wins over the job's snapshot: the field app edits
              clients.visit_notes, and an edit has to show on jobs that were ALREADY
              generated, not just future ones. Falls back to the snapshot when the client's
              is empty. Read-only here — the editable box further down is the note for
              THIS visit only. */}
          {(client?.visit_notes || primaryRow.visit_notes) && (
            <div style={{background:"#FFF8E6",border:"1px solid #F6D88A",borderRadius:8,padding:"9px 12px",marginBottom:14,display:"flex",gap:8,alignItems:"flex-start"}}>
              <span aria-hidden="true">📌</span>
              <div style={{minWidth:0}}>
                <div style={{fontSize:11,fontWeight:700,color:"#8A5A06",textTransform:"uppercase",letterSpacing:".05em"}}>Service note</div>
                <div style={{fontSize:13,color:"#5c3d02",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{client?.visit_notes || primaryRow.visit_notes}</div>
              </div>
            </div>
          )}
          {/* ── Client + address ───────────────────────────────────── */}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12,marginBottom:14}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#8993a4",textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}}>Client</div>
              <div style={{fontWeight:600,fontSize:14,color:"#1a1f36"}}>{client?.name || job.client_name}</div>
              {isPanel && onOpenClient && client && <button className="btn btn-ghost btn-sm sc-detail-profile" disabled={saving} onClick={() => { if (canLeave()) onOpenClient(client); }}>Open client profile ↗</button>}
              {/* Phone: the numbers + Maps link are what a dispatcher taps from a job — pills, not 12px text. */}
              {client?.phone && <div style={{fontSize:12,color:"#42526e",marginTop:isMobile?6:2}}><a href={`tel:${client.phone}`} style={isMobile?pillLink:{color:NAVY}}>{isMobile?"📞 ":""}{client.phone}</a></div>}
              {/* Second number also receives the ETA + completion texts. */}
              {client?.phone_secondary && <div style={{fontSize:12,color:"#42526e",marginTop:isMobile?6:0}}><a href={`tel:${client.phone_secondary}`} style={isMobile?pillLink:{color:NAVY}}>{isMobile?"📞 ":""}{client.phone_secondary}</a> <span style={{color:"#8993a4",fontSize:11}}>· 2nd</span></div>}
              {client?.email && <div style={{fontSize:12,color:"#42526e"}}>{client.email}</div>}
              <div style={{fontSize:11,color:"#8993a4",marginTop:4}}>{client?.dogs || job.dogs || 0}🐕 · {client?.frequency || "—"}{techName ? ` · 👤 ${techName}` : ""}</div>
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#8993a4",textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}}>Address</div>
              <div style={{fontSize:13,color:"#1a1f36"}}>{client?.address || job.client_address || "—"}</div>
              <div style={{fontSize:12,color:"#42526e"}}>
                {client?.city && `${client.city}, `}{client?.state || ""} {client?.zip || job.client_zip || ""}
              </div>
              {mapUrl && <a href={mapUrl} target="_blank" rel="noreferrer" style={isMobile?{...pillLink,marginTop:6}:{fontSize:11,color:NAVY,marginTop:2,display:"inline-block"}}>📍 Open in Maps</a>}
            </div>
          </div>

          {isPanel && source === "manual" && <div className="sc-manual-cost"><span>Recorded visit cost</span><strong>{services.reduce((total, service) => total + (Number(service.cost) || 0), 0).toLocaleString("en-US", { style: "currency", currency: "USD" })}</strong></div>}

          {/* ── Job timeline + DURATION (the whole job, from the header rollup) ── */}
          <TimingSection className={isPanel ? "sc-visit-timing" : undefined}>
          {isPanel && <summary>Visit timing &amp; GPS</summary>}
          {/* Phone: the 200px grid + two borderLeft blocks wrap into odd rows with stray
              left rules at 375px — stack the start/finish pair on top, then duration and
              time-on-site as an even two-up row under a single top rule. */}
          <div style={{background:"#f5f6f8",borderRadius:8,padding:"12px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:isMobile?"10px 0":14,flexWrap:"wrap"}}>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"minmax(0,1fr) minmax(0,1fr)":"repeat(2,auto)",gap:isMobile?"6px 12px":"6px 24px",flex:isMobile?"1 1 100%":1,minWidth:isMobile?0:200}}>
              <Field label="Job started"  value={fmtDateTime(jobStarted)} />
              <Field label="Job completed" value={fmtDateTime(jobCompleted)} />
            </div>
            <div style={isMobile?{...statCell,borderRight:"1px solid #e3e8ef"}:{textAlign:"center",padding:"4px 16px",borderLeft:"1px solid #e3e8ef"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#8993a4",textTransform:"uppercase",letterSpacing:".05em",marginBottom:2}}>Job duration</div>
              <div style={{fontFamily:"Inter",fontWeight:800,fontSize:22,color:inProgress ? "#974f0c" : NAVY}}>
                {inProgress ? "In progress" : fmtDuration(jobDuration)}
              </div>
              {jobDuration != null && !inProgress && <div style={{fontSize:10,color:"#8993a4",marginTop:1}}>start → last finish</div>}
            </div>
            {/* GPS time-on-site — a DISTINCT measure from the start→complete duration above */}
            <div style={isMobile?statCell:{textAlign:"center",padding:"4px 16px",borderLeft:"1px solid #e3e8ef"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#8993a4",textTransform:"uppercase",letterSpacing:".05em",marginBottom:2}}>🛰 Time on site</div>
              <div style={{fontFamily:"Inter",fontWeight:800,fontSize:22,color:onSiteMin != null ? "#0369a1" : "#c1c7d0"}}>
                {onSiteMin != null ? fmtDuration(onSiteMin) : "—"}
              </div>
              <div style={{fontSize:10,color:"#8993a4",marginTop:1}}>{onSiteMin != null ? (onSiteTech ? `GPS · ${onSiteTech}` : "GPS auto-tracked") : "no GPS visit"}</div>
            </div>
          </div>

          </TimingSection>

          {/* ── Services — the focus of the job; each is independently actionable ── */}
          <div style={{marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:700,color:"#8993a4",textTransform:"uppercase",letterSpacing:".05em"}}>
                Services ({services.length})
              </div>
              {scheduledSvc.length > 0 && (
                <button className="btn btn-gold btn-sm" style={{marginLeft:"auto"}} disabled={saving} onClick={handleStartJob}>
                  ▶ Start Job
                </button>
              )}
            </div>

            {loading && <div style={{fontSize:13,color:"#8993a4",padding:"8px 0"}}>Loading services…</div>}

            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {services.map(svc => {
                const terminal = TERMINAL_STATUSES.includes(svc.status);
                const dur = durationMins(svc.started_at, svc.completed_at);
                const amt = creditAmountFor(svc);
                return (
                  <div key={svc.id} style={{border:"1px solid #eef0f3",borderRadius:8,padding:"10px 12px",background:"#fff"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <span style={{fontSize:13,fontWeight:600,color:"#1a1f36"}}>{svcLabel(svc.service_type)}</span>
                      <span className={`badge ${statusBadgeClass(svc.status)}`} style={{textTransform:"capitalize"}}>{svc.status}</span>
                      {dur != null && <span style={{fontSize:11,color:"#8993a4"}}>⏱ {fmtDuration(dur)}</span>}
                      {svc.status === "completed" && svc.completed_at && <span style={{fontSize:11,color:"#8993a4"}}>· {fmtTime(svc.completed_at)}</span>}
                      {svc.credited && <span style={{fontSize:10,color:"#006644",background:"#e3fcef",padding:"1px 6px",borderRadius:4}}>💰 Credited</span>}
                      {svc.satisfaction_rating > 0 && <span style={{fontSize:11}}>{"⭐".repeat(svc.satisfaction_rating)}</span>}
                    </div>
                    {svc.skipped_reason && <div style={{fontSize:11,color:"#974f0c",marginTop:4}}>Skip reason: {skipReasonLabel(svc.skipped_reason)}{svc.skip_note ? ` — ${svc.skip_note}` : ""}</div>}

                    {/* per-service actions */}
                    {!terminal && skipFor !== svc.id && reschedFor !== svc.id && (
                      <div style={{display:"flex",gap:isMobile?8:6,flexWrap:"wrap",marginTop:8}}>
                        {/* Phone: the primary field actions fill the row at 40px so they are unmissable. */}
                        <button className="btn btn-gold btn-sm" style={actionBtn} disabled={saving} onClick={() => handleCompleteService(svc)}>✓ Complete</button>
                        <button className="btn btn-ghost btn-sm" style={actionBtn} disabled={saving} onClick={() => { setSkipReason(SKIP_REASONS[0]); setSkipNote(""); setSkipCredit(false); setReschedFor(null); setSkipFor(svc.id); }}>⏭ Skip</button>
                        <button className="btn btn-ghost btn-sm" style={actionBtn} disabled={saving} onClick={() => { setReschedDate(""); setSkipFor(null); setReschedFor(svc.id); }}>📅 Reschedule</button>
                      </div>
                    )}

                    {/* inline skip sub-flow */}
                    {skipFor === svc.id && (
                      <div style={{background:"#fff8e1",border:"1px solid "+GOLD,borderRadius:8,padding:"10px 12px",marginTop:8}}>
                        <div style={{fontWeight:700,fontSize:12,color:"#854d0e",marginBottom:8}}>⏭ Skip {svcShort(svc.service_type)}</div>
                        <div className="form-group" style={{marginBottom:8}}>
                          <label>Reason</label>
                          <select value={skipReason} onChange={e => setSkipReason(e.target.value)} disabled={saving}>
                            {SKIP_REASONS.map(r => <option key={r} value={r}>{skipReasonLabel(r)}</option>)}
                          </select>
                        </div>
                        <div className="form-group" style={{marginBottom:8}}>
                          <label>Skip note <span style={{color:"#8993a4",fontWeight:400}}>(optional)</span></label>
                          <textarea value={skipNote} onChange={e => setSkipNote(e.target.value)} maxLength={SKIP_NOTE_MAX} rows={2} disabled={saving} placeholder="Anything the office should know"/>
                        </div>
                        <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,marginBottom:10,cursor:amt>0?"pointer":"not-allowed",opacity:amt>0?1:0.55}}>
                          <input type="checkbox" checked={skipCredit && amt>0} disabled={saving || !(amt>0)} onChange={e => setSkipCredit(e.target.checked)}/>
                          {amt>0
                            ? <span>Credit the client <strong>${amt.toFixed(2)}</strong> for this service (applied to their next invoice)</span>
                            : <span>Credit unavailable — client plan/pricing not loaded</span>}
                        </label>
                        <div style={{display:"flex",gap:8}}>
                          <button className="btn btn-gold btn-sm" disabled={saving} onClick={() => handleConfirmSkip(svc)}>{saving ? "Saving…" : "Confirm Skip"}</button>
                          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setSkipFor(null)}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {/* inline reschedule sub-flow */}
                    {reschedFor === svc.id && (
                      <div style={{background:"#eef2ff",border:"1px solid #c7d2fe",borderRadius:8,padding:"10px 12px",marginTop:8}}>
                        <div style={{fontWeight:700,fontSize:12,color:"#3730a3",marginBottom:6}}>📅 Reschedule {svcShort(svc.service_type)}</div>
                        <div style={{fontSize:11,color:"#8993a4",marginBottom:8}}>
                          Moves this service to a later date. The original day keeps a "rescheduled" marker. This is <strong>not a skip</strong> — no credit is issued.
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:13,color:"#42526e"}}>New date</span>
                          <input type="date" value={reschedDate} min={day} onChange={e => setReschedDate(e.target.value)} disabled={saving} style={isMobile?{flex:1,minWidth:0}:undefined}/>
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <button className="btn btn-gold btn-sm" disabled={saving || !reschedDate} onClick={() => handleReschedule(svc)}>{saving ? "Rescheduling…" : "Confirm Reschedule"}</button>
                          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setReschedFor(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {!loading && services.length === 0 && <div style={{fontSize:13,color:"#8993a4"}}>No active services on this job.</div>}
            </div>
          </div>

          {/* ── Proof photo (one per job, from the header) ─────────── */}
          {photoPath && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:"#8993a4",textTransform:"uppercase",letterSpacing:".05em",marginBottom:6}}>Proof photo</div>
              <JobPhoto path={photoPath} size={120} />
            </div>
          )}

          {/* ── Send ETA (job-level) ───────────────────────────────── */}
          {!allTerminal && (
            <div style={{marginBottom:14}}>
              {!etaMode ? (
                <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setEtaMode(true)}>📲 Send ETA</button>
              ) : (
                <div style={{background:"#deebff",border:"1px solid #b3d4ff",borderRadius:8,padding:"12px 14px"}}>
                  <div style={{fontWeight:700,fontSize:13,color:"#0747a6",marginBottom:8}}>📲 Send ETA to client</div>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <span style={{fontSize:13,color:"#42526e"}}>Tech arriving in</span>
                    <input type="number" min="1" max="240" value={etaMinutes} onChange={e => setEtaMinutes(Number(e.target.value))} disabled={saving} style={{width:80,textAlign:"center"}}/>
                    <span style={{fontSize:13,color:"#42526e"}}>minutes</span>
                  </div>
                  <div style={{fontSize:11,color:"#8993a4",marginBottom:10}}>{isPanel ? "Sends by text or email when available and records the result in client history." : <>Logged to client history. Once SMS/email is wired up, this will also notify {client?.name || "the client"}.</>}</div>
                  <div style={{display:"flex",gap:8}}>
                    <button className="btn btn-gold btn-sm" disabled={saving || !etaMinutes} onClick={handleSendETA}>Send ETA</button>
                    <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setEtaMode(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tech assignment (job-level; office-only) ───────────── */}
          {!techScoped && (
          <div className="form-group" style={{marginBottom:14}}>
            <label>Assigned Technician <span style={{fontSize:10,color:"#8993a4",fontWeight:400}}>(whole job — for sick-day reassign)</span></label>
            <select value={techId} onChange={e => setTechId(e.target.value)} disabled={saving || allTerminal}>
              <option value="">— Unassigned —</option>
              {(technicians || []).filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {techDirty && !allTerminal && (
              <div style={{marginTop:8,display:"flex",gap:8}}>
                <button className="btn btn-gold btn-sm" disabled={saving} onClick={handleApplyTech}>{saving ? "Applying…" : "Apply Tech Change"}</button>
                <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setTechId(serverTech)}>Cancel</button>
              </div>
            )}
          </div>
          )}

          {/* ── Notes (job-level) ──────────────────────────────────── */}
          {!techScoped && (
          <div className="form-group" style={{marginBottom:10}}>
            <label>Note to Office <span style={{fontSize:10,color:"#8993a4",fontWeight:400}}>(internal — not shown to customer)</span></label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} disabled={saving} placeholder="Anything internal — billing notes, tech context..." style={{minHeight:60}}/>
          </div>
          )}
          <div className="form-group" style={{marginBottom:10}}>
            <label>Visit Notes <span style={{fontSize:10,color:"#8993a4",fontWeight:400}}>(tech-facing on the route + on client receipts)</span></label>
            <textarea value={visitNotes} onChange={e => setVisitNotes(e.target.value)} disabled={saving || techScoped} placeholder="Gate code, dog temperament, where to leave bags..." style={{minHeight:60}}/>
          </div>
          {!techScoped && notesDirty && (
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              <button className="btn btn-gold btn-sm" disabled={saving} onClick={handleSaveNotes}>{saving ? "Saving…" : "Save Notes"}</button>
              <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => { setNotes(serverNotes); setVisitNotes(serverVisit); }}>Discard</button>
            </div>
          )}

          {error && <div style={{background:"#ffebe6",border:"1px solid #ffbdad",borderRadius:6,padding:"8px 12px",fontSize:12,color:"#bf2600",marginTop:10}}>⚠ {error}</div>}
          </>}
        </div>

        <div className="modal-footer">
          {isPanel && hasUnsavedChanges && <span className="sc-detail-unsaved" role="status">Unsaved changes</span>}
          <button className="btn btn-ghost" onClick={requestClose} disabled={isPanel && saving}>Close</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, highlight }) {
  const color = highlight === "warn" ? "#974f0c" : "#1a1f36";
  return (
    <div>
      <div style={{fontSize:10,fontWeight:700,color:"#8993a4",textTransform:"uppercase",letterSpacing:".05em",marginBottom:2}}>{label}</div>
      <div style={{fontSize:13,fontWeight:600,color}}>{value || "—"}</div>
    </div>
  );
}
